import type {
  AuthorizationUser,
  Permission,
  Resource,
  RoleResource,
  RoleResourcePermission,
  VmsRole,
} from "./types";

/** API returns mapping PK as `id`; column `roleId` holds the grantee role_resource id. */
export function normalizeRoleResourcePermission(row: Record<string, unknown>): RoleResourcePermission {
  const id = Number(row.id ?? row.roleResourcePermissionId ?? 0);
  const granteeRoleResourceId = Number(row.roleId ?? 0);
  return {
    roleResourcePermissionId: id,
    granteeRoleResourceId,
    resourceId: Number(row.resourceId ?? 0),
    permissionId: Number(row.permissionId ?? 0),
    createdAt: row.createdAt as string | undefined,
    updatedAt: row.updatedAt as string | undefined,
  };
}

export function normalizeRoleResourceRow(row: Record<string, unknown>): RoleResource {
  return {
    roleResourceId: Number(row.roleResourceId ?? 0),
    roleId: Number(row.roleId ?? 0),
    resourceId: Number(row.resourceId ?? 0),
    role: String(row.role ?? ""),
    description: String(row.description ?? ""),
    parentId: row.parentId == null ? null : Number(row.parentId),
    createdAt: row.createdAt as string | undefined,
    updatedAt: row.updatedAt as string | undefined,
  };
}

export function normalizeAuthorizationUser(row: Record<string, unknown>): AuthorizationUser {
  return {
    userId: Number(row.userId ?? 0),
    name: String(row.name ?? ""),
    email: String(row.email ?? ""),
    roleId: Number(row.roleId ?? 0),
    roleResourceId: Number(row.roleResourceId ?? 0),
    resourceId: row.resourceId == null ? undefined : Number(row.resourceId),
    subRole: row.subRole == null ? undefined : String(row.subRole),
    createdAt: row.createdAt as string | undefined,
    updatedAt: row.updatedAt as string | undefined,
  };
}

export function asArray<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  return [];
}

export function toVmsRole(row: Record<string, unknown>): VmsRole {
  return {
    roleId: Number(row.roleId ?? 0),
    role: String(row.role ?? ""),
    description: String(row.description ?? ""),
    createdAt: row.createdAt as string | undefined,
    updatedAt: row.updatedAt as string | undefined,
    permissions: row.permissions as Record<string, unknown> | undefined,
  };
}

export function toPermission(row: Record<string, unknown>): Permission {
  return {
    permissionId: Number(row.permissionId ?? 0),
    action: String(row.action ?? ""),
    scope: String(row.scope ?? ""),
    createdAt: row.createdAt as string | undefined,
    updatedAt: row.updatedAt as string | undefined,
  };
}

export function toResource(row: Record<string, unknown>): Resource {
  return {
    resourceId: Number(row.resourceId ?? 0),
    resourceName: String(row.resourceName ?? ""),
    parentRole: Number(row.parentRole ?? 0),
    createdAt: row.createdAt as string | undefined,
    updatedAt: row.updatedAt as string | undefined,
  };
}

export function mergeRoleResourcesById(
  primary: RoleResource[],
  secondary: RoleResource[]
): RoleResource[] {
  const map = new Map<number, RoleResource>();
  for (const r of primary) map.set(r.roleResourceId, r);
  for (const r of secondary) {
    if (!map.has(r.roleResourceId)) map.set(r.roleResourceId, r);
  }
  return Array.from(map.values());
}
